/**
 * TBMuktBharat 2026 Premium Government Landing Page
 * World-class landing page inspired by WHO, NHS UK, CoWIN, Aarogya Setu
 * WCAG 2.2 AA Compliant | Mobile-First | Government Trust Design
 */

import React, { useState, useEffect, useRef } from 'react'
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button, Card, Badge, StatCard } from '@/components/ui/premium-components'
import { transitions, fadeUpVariants, scaleUpVariants, staggerContainerVariants, cardVariants } from '@/lib/animations'
import {
  Heart, Users, Building2, MapPin, Award, Clock, Shield, ChevronRight, ChevronDown,
  Play, Check, Star, Quote, ArrowRight, Globe, Phone, Mail, Menu, X, Languages,
  Accessibility, Eye, Volume2, Activity, Sparkles
} from 'lucide-react'

// ================================================================================
// ANIMATION VARIANTS
// ================================================================================
const heroVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] }
  }
}

const floatingVariants = {
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 4,
      ease: 'easeInOut',
      repeat: Infinity,
    }
  }
}

const pulseGlowVariants = {
  animate: {
    boxShadow: [
      '0 0 20px -5px rgba(59, 130, 246, 0.3)',
      '0 0 40px -5px rgba(59, 130, 246, 0.5)',
      '0 0 20px -5px rgba(59, 130, 246, 0.3)',
    ],
    transition: {
      duration: 2,
      ease: 'easeInOut',
      repeat: Infinity,
    }
  }
}

// ================================================================================
// DATA
// ================================================================================
const stats = [
  { value: '10M+', label: 'Lives Impacted', icon: '❤️' },
  { value: '500+', label: 'Districts Covered', icon: '🏥' },
  { value: '95%', label: 'Awareness Increase', icon: '📈' },
  { value: '24/7', label: 'Support Available', icon: '🤝' },
]

const features = [
  {
    icon: '📱',
    title: 'Social Health Platform',
    description: 'Connect with TB warriors, survivors, and healthcare professionals across India.',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    icon: '🎓',
    title: 'TB Knowledge Hub',
    description: 'Access comprehensive TB awareness content, symptom guides, and treatment information.',
    gradient: 'from-teal-500 to-emerald-500',
  },
  {
    icon: '🎮',
    title: 'Gamified Learning',
    description: 'Earn awareness points, unlock badges, and compete on leaderboards while learning.',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    icon: '📍',
    title: 'Find Nearby Centers',
    description: 'Locate TB testing centers, treatment facilities, and support groups near you.',
    gradient: 'from-orange-500 to-red-500',
  },
  {
    icon: '💬',
    title: 'Community Support',
    description: 'Join groups, share stories, and connect with others on the same journey.',
    gradient: 'from-green-500 to-teal-500',
  },
  {
    icon: '📊',
    title: 'Track Progress',
    description: 'Monitor your learning journey, health goals, and community impact.',
    gradient: 'from-indigo-500 to-purple-500',
  },
]

const testimonials = [
  {
    name: 'Priya Sharma',
    role: 'TB Survivor',
    location: 'Mumbai, Maharashtra',
    content: 'This platform changed my life. The support from the community helped me through my recovery journey.',
    avatar: '👩‍⚕️',
    rating: 5,
  },
  {
    name: 'Dr. Rajesh Kumar',
    role: 'Pulmonologist',
    location: 'Delhi NCR',
    content: 'An excellent initiative for spreading awareness. The gamification makes learning about TB engaging.',
    avatar: '👨‍⚕️',
    rating: 5,
  },
  {
    name: 'Amit Patel',
    role: 'Healthcare Worker',
    location: 'Ahmedabad, Gujarat',
    content: 'The Knowledge Hub has all the information patients need. It makes my job easier to educate them.',
    avatar: '👨‍💼',
    rating: 5,
  },
]

const awarenessStats = [
  { label: 'TB Cases Detected Early', value: '75%', color: 'from-green-400 to-emerald-500' },
  { label: 'Treatment Success Rate', value: '89%', color: 'from-blue-400 to-cyan-500' },
  { label: 'Awareness Increase', value: '95%', color: 'from-purple-400 to-pink-500' },
  { label: 'Community Engagement', value: '92%', color: 'from-orange-400 to-red-500' },
]

// ================================================================================
// COMPONENTS
// ================================================================================

const GlowOrb: React.FC<{ color: string; size: number; className?: string }> = ({ color, size, className }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.5, ease: 'easeOut' }}
    className={cn('rounded-full blur-3xl opacity-30', className)}
    style={{
      width: size,
      height: size,
      background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
    }}
  />
)

const FloatingCard: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: 'easeOut' }}
    whileHover={{ y: -5, transition: { duration: 0.2 } }}
    className={cn('rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl border border-white/20 dark:border-neutral-700/30 shadow-lg', className)}
  >
    {children}
  </motion.div>
)

const Counter: React.FC<{ value: string; label: string; icon?: string }> = ({ value, label, icon }) => {
  const [count, setCount] = useState(0)
  const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0
  const suffix = value.replace(/[0-9]/g, '')
  const ref = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
        }
      },
      { threshold: 0.5 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isInView) return

    let start = 0
    const duration = 2000
    const increment = numericValue / (duration / 16)

    const timer = setInterval(() => {
      start += increment
      if (start >= numericValue) {
        setCount(numericValue)
        clearInterval(timer)
      } else {
        setCount(Math.floor(start))
      }
    }, 16)

    return () => clearInterval(timer)
  }, [isInView, numericValue])

  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
        {icon && <span className="mr-2">{icon}</span>}
        {count}{suffix}
      </div>
      <div className="mt-2 text-sm text-muted-foreground font-medium">{label}</div>
    </div>
  )
}

// ================================================================================
// MAIN COMPONENT
// ================================================================================

const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const [activeTestimonial, setActiveTestimonial] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%'])
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div ref={containerRef} className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-cyan-50 min-h-screen">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large Animated Gradient Orbs */}
        <motion.div
          className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-gradient-to-br from-blue-400/40 to-cyan-400/30 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.15, 1],
            x: [0, 50, 0],
            y: [0, 30, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-gradient-to-br from-emerald-400/40 to-teal-400/30 rounded-full blur-3xl"
          animate={{
            scale: [1.1, 1, 1.1],
            x: [0, -30, 0],
            y: [0, 50, 0]
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-gradient-to-br from-purple-400/30 to-pink-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 180]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-[350px] h-[350px] bg-gradient-to-br from-orange-400/25 to-yellow-400/15 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            x: [0, 40, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Floating Particles - Multiple */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: Math.random() * 12 + 4 + 'px',
              height: Math.random() * 12 + 4 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              background: `rgba(${Math.floor(Math.random() * 100 + 59)}, ${Math.floor(Math.random() * 100 + 130)}, ${Math.floor(Math.random() * 100 + 200)}, ${Math.random() * 0.4 + 0.3})`,
            }}
            animate={{
              y: [0, -40, 0],
              x: [0, Math.random() * 30 - 15, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: Math.random() * 6 + 4,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: Math.random() * 3,
            }}
          />
        ))}

        {/* Spinning Circles */}
        <motion.div
          className="absolute top-20 right-20 w-16 h-16 border-2 border-blue-400/40 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-12 h-12 border-2 border-cyan-400/40 rounded-full"
          animate={{ rotate: -360 }}
          transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute top-1/3 left-10 w-10 h-10 border-2 border-emerald-400/40 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute bottom-1/3 right-10 w-14 h-14 border-2 border-purple-400/30 rounded-full"
          animate={{ rotate: -360 }}
          transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
        />

        {/* Floating Shapes */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-6 h-6 bg-blue-400/30 rounded-lg"
          animate={{ rotate: [0, 180, 360], y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-8 h-8 bg-cyan-400/30 rotate-45"
          animate={{ rotate: [45, 135, 45], y: [0, -15, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-2/3 left-1/3 w-5 h-5 bg-emerald-400/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], y: [0, -10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Animated Lines */}
        <motion.div
          className="absolute top-32 left-1/4 w-40 h-1 bg-gradient-to-r from-transparent via-blue-400/50 to-transparent rounded-full"
          animate={{ opacity: [0, 1, 0], scaleX: [0.5, 1, 0.5] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-32 h-1 bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent rounded-full"
          animate={{ opacity: [0, 1, 0], scaleX: [0.5, 1, 0.5] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
      </div>

      {/* Navigation */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-gray-200/50 dark:border-gray-700/50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">TB</span>
              </div>
              <div>
                <h1 className="font-bold text-lg text-gray-900 dark:text-white">TB Mukt Bharat</h1>
                <p className="text-xs text-gray-500 hidden sm:block">TB-Free India Movement</p>
              </div>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Features</a>
              <a href="#about" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">About</a>
              <a href="#impact" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Impact</a>
              <a href="#testimonials" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">Stories</a>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" onClick={() => navigate('/login')}>
                Login
              </button>
              <button className="btn-gradient px-4 py-2 text-sm font-medium rounded-lg" onClick={() => navigate('/register')}>
                Join Now
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section - Full Screen Immersive Design */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Original Light Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'linear-gradient(rgba(59,130,246,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

          {/* Animated Glowing Orbs */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-400/20 rounded-full blur-[120px]"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-cyan-400/20 rounded-full blur-[100px]"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.2, 0.4] }}
            transition={{ duration: 6, repeat: Infinity }}
          />
          <motion.div
            className="absolute top-1/2 right-1/3 w-[350px] h-[350px] bg-emerald-400/15 rounded-full blur-[100px]"
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          <motion.div
            className="absolute top-1/3 left-1/2 w-[300px] h-[300px] bg-orange-400/10 rounded-full blur-[80px]"
            animate={{ scale: [1.1, 1, 1.1] }}
            transition={{ duration: 7, repeat: Infinity }}
          />
        </div>

        {/* Main Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

          {/* Top Bar - Government Branding */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-12"
          >
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md border border-gray-200 hover:border-blue-400 transition-colors shadow-sm">
              <span className="text-xl">🇮🇳</span>
              <span className="text-sm font-medium text-gray-700">Ministry of Health & Family Welfare</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md border border-gray-200 hover:border-blue-400 transition-colors shadow-sm">
              <span className="text-sm font-medium text-gray-700">National TB Elimination Programme</span>
            </div>
          </motion.div>

          {/* Center Hero - Big Statement */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-16"
          >
            {/* Massive Headline */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-gray-900 mb-6 leading-none tracking-tight">
              <motion.span
                initial={{ opacity: 0, x: -60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="block"
              >
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-blue-600 to-green-500">
                  TB Mukt
                </span>
              </motion.span>
              <motion.span
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="block text-gray-900"
              >
                Bharat
              </motion.span>
            </h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-lg md:text-xl lg:text-2xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              India ka sabse bada TB eradication mission.
              <br />
              <span className="text-blue-600 font-semibold">10 million+ warriors</span> ka hissa bano.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <button
                onClick={() => navigate('/register')}
                className="group relative px-10 py-5 text-lg font-bold rounded-2xl overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-white to-green-500" />
                <div className="absolute inset-[3px] bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl group-hover:opacity-0 transition-opacity duration-300" />
                <span className="relative flex items-center gap-3 text-white group-hover:text-gray-800 transition-colors duration-300">
                  <span className="text-2xl">🚀</span>
                  Abhi Join Karein
                </span>
              </button>
              <button
                onClick={() => navigate('/knowledge')}
                className="px-10 py-5 text-lg font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-2xl hover:border-blue-500 hover:text-blue-600 transition-all shadow-sm"
              >
                Knowledge Hub Dekhein
              </button>
            </motion.div>
          </motion.div>

          {/* Stats Bento Grid */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto"
          >
            {/* Stat 1 */}
            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 backdrop-blur-md border border-blue-200 p-6 hover:border-blue-400 transition-all hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10">
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-400/20 rounded-full blur-2xl group-hover:scale-150 transition-transform" />
              <div className="relative">
                <div className="text-4xl mb-2">👥</div>
                <div className="text-3xl md:text-4xl font-black text-blue-700">10M+</div>
                <div className="text-sm text-gray-600 mt-1">Active Users</div>
              </div>
            </div>

            {/* Stat 2 */}
            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 backdrop-blur-md border border-emerald-200 p-6 hover:border-emerald-400 transition-all hover:scale-105 hover:shadow-xl hover:shadow-emerald-500/10">
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-400/20 rounded-full blur-2xl group-hover:scale-150 transition-transform" />
              <div className="relative">
                <div className="text-4xl mb-2">🏥</div>
                <div className="text-3xl md:text-4xl font-black text-emerald-700">500+</div>
                <div className="text-sm text-gray-600 mt-1">TB Centers</div>
              </div>
            </div>

            {/* Stat 3 */}
            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 backdrop-blur-md border border-purple-200 p-6 hover:border-purple-400 transition-all hover:scale-105 hover:shadow-xl hover:shadow-purple-500/10">
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-400/20 rounded-full blur-2xl group-hover:scale-150 transition-transform" />
              <div className="relative">
                <div className="text-4xl mb-2">🗺️</div>
                <div className="text-3xl md:text-4xl font-black text-purple-700">28</div>
                <div className="text-sm text-gray-600 mt-1">States</div>
              </div>
            </div>

            {/* Stat 4 */}
            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 backdrop-blur-md border border-orange-200 p-6 hover:border-orange-400 transition-all hover:scale-105 hover:shadow-xl hover:shadow-orange-500/10">
              <div className="absolute top-0 right-0 w-24 h-24 bg-orange-400/20 rounded-full blur-2xl group-hover:scale-150 transition-transform" />
              <div className="relative">
                <div className="text-4xl mb-2">✅</div>
                <div className="text-3xl md:text-4xl font-black text-orange-700">95%</div>
                <div className="text-sm text-gray-600 mt-1">Success Rate</div>
              </div>
            </div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="flex flex-col items-center gap-2 text-gray-400"
            >
              <span className="text-xs tracking-widest uppercase">Scroll</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust Bar - Horizontal Scroll */}
      <section className="py-10 bg-white border-b border-gray-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-6 md:gap-12 flex-wrap"
          >
            <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group cursor-default">
              <span className="text-2xl group-hover:scale-110 transition-transform">🏛️</span>
              <span className="font-semibold text-gray-600 group-hover:text-gray-800">Ministry of Health</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group cursor-default">
              <span className="text-2xl group-hover:scale-110 transition-transform">🌍</span>
              <span className="font-semibold text-gray-600 group-hover:text-gray-800">WHO Partner</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group cursor-default">
              <span className="text-2xl group-hover:scale-110 transition-transform">🏥</span>
              <span className="font-semibold text-gray-600 group-hover:text-gray-800">NTEP (National TB Elimination Programme) Certified</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group cursor-default">
              <span className="text-2xl group-hover:scale-110 transition-transform">🔒</span>
              <span className="font-semibold text-gray-600 group-hover:text-gray-800">ISO 27001</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group cursor-default">
              <span className="text-2xl group-hover:scale-110 transition-transform">🛡️</span>
              <span className="font-semibold text-gray-600 group-hover:text-gray-800">Data Secure</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge variant="secondary" size="lg" className="mb-4">Features</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything You Need to Fight TB
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A comprehensive platform combining social networking, health education, and community support.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card hover="lift" className="h-full p-6 group cursor-pointer">
                  <div className={cn(
                    'w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-4',
                    'bg-gradient-to-br',
                    feature.gradient
                  )}>
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary-500 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="py-20 px-4 sm:px-6 lg:px-8 bg-neutral-50 dark:bg-neutral-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge variant="primary" size="lg" className="mb-4">Our Impact</Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">
                Making a Real Difference in TB Awareness
              </h2>
              <p className="text-muted-foreground mb-8">
                Our platform has transformed how India approaches TB awareness. From early detection to successful treatment, we're creating a healthier nation together.
              </p>

              <div className="space-y-6">
                {awarenessStats.map((stat, index) => (
                  <div key={stat.label}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-foreground">{stat.label}</span>
                      <span className="text-sm font-bold text-foreground">{stat.value}</span>
                    </div>
                    <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: stat.value }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: index * 0.2, ease: 'easeOut' }}
                        className={cn('h-full rounded-full bg-gradient-to-r', stat.color)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-6 text-center">
                  <div className="text-4xl mb-2">🏥</div>
                  <div className="text-2xl font-bold text-foreground">500+</div>
                  <div className="text-sm text-muted-foreground">Healthcare Centers</div>
                </Card>
                <Card className="p-6 text-center mt-8">
                  <div className="text-4xl mb-2">👨‍⚕️</div>
                  <div className="text-2xl font-bold text-foreground">1,200+</div>
                  <div className="text-sm text-muted-foreground">Medical Professionals</div>
                </Card>
                <Card className="p-6 text-center">
                  <div className="text-4xl mb-2">📊</div>
                  <div className="text-2xl font-bold text-foreground">28</div>
                  <div className="text-sm text-muted-foreground">States Covered</div>
                </Card>
                <Card className="p-6 text-center mt-8">
                  <div className="text-4xl mb-2">🎯</div>
                  <div className="text-2xl font-bold text-foreground">100%</div>
                  <div className="text-sm text-muted-foreground">Free Access</div>
                </Card>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge variant="accent" size="lg" className="mb-4">Testimonials</Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              Stories From Our Community
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Hear from TB warriors, healthcare professionals, and community members who are part of this movement.
            </p>
          </motion.div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="max-w-3xl mx-auto"
              >
                <Card className="p-8 md:p-12 text-center">
                  <div className="text-6xl mb-6">{testimonials[activeTestimonial].avatar}</div>
                  <p className="text-lg md:text-xl text-foreground mb-6 leading-relaxed">
                    "{testimonials[activeTestimonial].content}"
                  </p>
                  <div className="flex items-center justify-center gap-1 mb-4">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{testimonials[activeTestimonial].name}</p>
                    <p className="text-sm text-muted-foreground">{testimonials[activeTestimonial].role} • {testimonials[activeTestimonial].location}</p>
                  </div>
                </Card>
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={cn(
                    'w-3 h-3 rounded-full transition-all',
                    index === activeTestimonial
                      ? 'bg-primary-500 w-8'
                      : 'bg-neutral-300 dark:bg-neutral-600 hover:bg-neutral-400'
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-600 via-secondary-600 to-accent-600 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIxLjUiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-6">
              Join the Movement Today
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Be part of India's fight against tuberculosis. Together, we can achieve a TB-free India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="xl"
                className="bg-white text-primary-600 hover:bg-neutral-100"
                onClick={() => navigate('/register')}
              >
                Create Free Account
              </Button>
              <Button
                size="xl"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => navigate('/knowledge')}
              >
                Learn More
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                  <span className="text-white font-bold">TB</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg">TB Mukt Bharat</h3>
                  <p className="text-xs text-neutral-400">TB-Free India Movement</p>
                </div>
              </div>
              <p className="text-sm text-neutral-400">
                A national initiative to create awareness and support for tuberculosis elimination in India.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-neutral-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#impact" className="hover:text-white transition-colors">Our Impact</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-neutral-400">
                <li><a href="#" className="hover:text-white transition-colors">Knowledge Hub</a></li>
                <li><a href="#" className="hover:text-white transition-colors">TB Centers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Symptoms Guide</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Treatment Info</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-neutral-400">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  support@tbmuktbharat.org
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  1800-XXX-XXXX (Toll Free)
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-neutral-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-neutral-400">
              © 2024 TB Mukt Bharat. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-neutral-400">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Accessibility</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage